import { Component, ChangeDetectionStrategy, signal, HostListener, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PartnerService } from '../../core/services/partner.service';
import { LogoComponent } from '../../shared/components/logo/logo.component';
import { ChatWidgetComponent } from '../../chat/chat-widget/chat-widget.component';

@Component({
  selector: 'app-shell-layout',
  standalone: true,
  imports: [CommonModule, RouterOutlet, LogoComponent, ChatWidgetComponent],
  templateUrl: './shell.layout.html',
  styleUrl: './shell.layout.css',
  changeDetection: ChangeDetectionStrategy.Default
})
export class ShellLayout {
  private readonly destroyRef = inject(DestroyRef);
  protected readonly isDropdownOpen = signal(false);

  protected get userName(): string {
    return this.partnerService.companyName() || 'Partner';
  }

  protected get userEmail(): string {
    return this.partnerService.companyEmail() || 'partner@hashbooks.io';
  }

  protected get emailInitials(): string {
    const name = this.userName;
    if (!name || name === 'Partner' || name === 'Hashbooks Global Partner') {
      return 'PA';
    }
    const cleanName = name.trim();
    const parts = cleanName.split(/\s+/);
    if (parts.length > 1) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return cleanName.slice(0, 2).toUpperCase();
  }

  protected toggleDropdown(event: Event) {
    event.stopPropagation();
    this.isDropdownOpen.set(!this.isDropdownOpen());
  }

  @HostListener('document:click')
  protected closeDropdown() {
    this.isDropdownOpen.set(false);
  }
  constructor(
    protected readonly partnerService: PartnerService,
    private readonly router: Router
  ) {
    // If not logged in, redirect to landing page
    if (!this.partnerService.isLoggedIn()) {
      this.router.navigate(['/']);
    }

    // Scroll dashboard content container smoothly to top on navigation
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      const mainEl = document.querySelector('.dashboard-main');
      if (mainEl) {
        mainEl.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  }

  protected isActive(route: string): boolean {
    return this.router.url.includes(route);
  }

  protected navigateTo(route: string) {
    this.router.navigate([`/app/${route}`]);
  }

  protected handleLogout() {
    this.partnerService.handleLogout();
  }
}
